<script setup lang="ts">
  import { computed, onMounted, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { BookOpen, X } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { useSurveyStore } from "@stores/survey/survey.js";
  import { useSurveyContextStore } from "@stores/survey/surveyContext";

  const { token, canWriteSurvey } = storeToRefs(useSurveyStore());
  const surveyContextStore = useSurveyContextStore();
  const { context } = storeToRefs(surveyContextStore);

  const storageKey = computed(() => `survey_context_banner_dismissed_${token.value}`);
  const readDismissed = () => {
    try {
      return localStorage.getItem(storageKey.value) === "1";
    } catch {
      return false;
    }
  };
  const dismissed = ref(readDismissed());

  const visible = computed(
    () => canWriteSurvey.value && !dismissed.value && context.value && !context.value.content?.trim(),
  );

  const storeDismissed = () => {
    try {
      localStorage.setItem(storageKey.value, "1");
      return true;
    } catch {
      return false;
    }
  };

  const dismiss = () => {
    dismissed.value = true;
    storeDismissed();
  };

  onMounted(async () => {
    if (!canWriteSurvey.value || dismissed.value) return;

    try {
      await surveyContextStore.fetch(String(token.value));
    } catch {
      context.value = null;
    }
  });
</script>

<template>
  <div
    v-if="visible"
    role="status"
    data-cy="survey-context-banner"
    class="flex items-start gap-3 rounded-lg border border-status-info-border bg-status-info-soft py-3 pr-3 pl-3.5 text-left">
    <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-background text-status-info-strong">
      <BookOpen
        class="size-4"
        :stroke-width="1.8" />
    </span>
    <div class="flex min-w-0 flex-1 flex-col gap-0.5">
      <span class="text-13 font-semibold text-status-info-strong">Answers are sharper with the study context</span>
      <span class="text-13 leading-normal text-status-info-strong">
        Add the brief or kick-off notes so Episto AI knows your objectives, target and hypotheses.
      </span>
    </div>
    <Button
      as-child
      size="sm"
      class="self-center">
      <RouterLink :to="{ name: 'SurveyContext', params: { token } }">Add context</RouterLink>
    </Button>
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label="Dismiss"
      class="self-center text-status-info-strong"
      @click="dismiss">
      <X />
    </Button>
  </div>
</template>
